'use client';
import './LocalizedField.css';
import SlTextarea from '@shoelace-style/shoelace/dist/react/textarea/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip/index.js';
import { useContext, useState } from 'react';
import { LocaleCode, TextsToLocalize } from '../../core/translation';
import { localizedFieldsetContext } from '../LocalizedFieldset/LocalizedFieldset';

interface LocalizedFieldProps {
	texts: TextsToLocalize;
	label: string;
	initialLanguage?: LocaleCode;
	isRequired?: boolean;
	isTextarea?: boolean;
	showCharCount?: boolean;
	onInputText?: (text: string, language: LocaleCode) => void;
	t?: any;
	[key: string]: any;
}

const LocalizedField = ({
	texts,
	label,
	initialLanguage,
	isRequired = false,
	isTextarea = true,
	showCharCount = false,
	onInputText,
	t,
	...rest
}: LocalizedFieldProps) => {
	const [selectedLanguage, setSelectedLanguage] = useState<LocaleCode>(initialLanguage || 'en');

	const fieldsetContext = useContext(localizedFieldsetContext);

	const isInFieldset = fieldsetContext != null;
	let code = selectedLanguage;
	if (isInFieldset) {
		code = fieldsetContext.language;
	}

	const onInput = (e: any) => {
		if (onInputText) {
			onInputText(e.target.value, code);
		}
	};

	const onSelectLanguange = (language: LocaleCode) => {
		setSelectedLanguage(language);
	};

	const onTooltipAfterHide = (e: any) => {
		// Prevent the 'sl-hide' event from bubbling up. This is
		// necessary to prevent the unexpected hiding of the parent
		// elements such as drawers, dialog etc.
		e.stopPropagation();
	};

	return (
		<div className='localized-field'>
			<div className='localized-field__head'>
				<div className='localized-field__label'>
					{label}
					{isRequired && <span className='localized-field__asterisk'>*</span>}
				</div>
				{showCharCount && (
					<span className='localized-field__char-count'>{[...(texts[code] ?? '')].length}</span>
				)}
				{!isInFieldset && (
					<div className='localized-field__flags'>
						<div
							className={`localized-field__flag${
								code === 'it' ? ' localized-field__flag--selected' : ''
							}`}
							onClick={() => onSelectLanguange('it')}
						>
							<SlTooltip
								content={t == null ? 'Italiano' : t('Italian')}
								hoist
								onSlAfterHide={onTooltipAfterHide}
							>
								<img src='/assets/italy.svg' />
							</SlTooltip>
						</div>
						<div
							className={`localized-field__flag${
								code === 'en' ? ' localized-field__flag--selected' : ''
							}`}
							onClick={() => onSelectLanguange('en')}
						>
							<SlTooltip
								content={t == null ? 'Inglese' : t('English')}
								hoist
								onSlAfterHide={onTooltipAfterHide}
							>
								<img src={'/assets/united-kingdom.svg'} />
							</SlTooltip>
						</div>
						<div
							className={`localized-field__flag${
								code === 'fr' ? ' localized-field__flag--selected' : ''
							}`}
							onClick={() => onSelectLanguange('fr')}
						>
							<SlTooltip
								content={t == null ? 'Francese' : t('French')}
								hoist
								onSlAfterHide={onTooltipAfterHide}
							>
								<img src={'/assets/france.svg'} />
							</SlTooltip>
						</div>
					</div>
				)}
			</div>
			{isTextarea ? (
				<SlTextarea
					{...rest}
					spellCheck={false}
					autoCorrect='off'
					value={texts[code] == null ? '' : texts[code]}
					onSlInput={onInput}
					resize='none'
				/>
			) : (
				<SlInput
					{...rest}
					spellCheck={false}
					autoCorrect='off'
					value={texts[code] == null ? '' : texts[code]}
					onSlInput={onInput}
				/>
			)}
		</div>
	);
};

export { LocalizedField };
